import {
  CustomLayoutDrawerContent,
  DrawerBreadCrumbs,
} from 'module/ui/components/atoms/drawer';
import { Form } from 'module/ui/components/atoms/form';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { Loading } from 'module/ui/components/atoms/loading';
import { faCheck, faGlobe } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { PageTemplateLite } from 'api/page-template/types';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { PageTemplateSourceList } from 'components/organisms/template-sources/source-list';
import { PageTemplateSourceSelector } from 'components/organisms/template-sources/source-selector';
import { useTemplatePageSources } from 'components/organisms/template-sources/use-sources';
import { PeoplePageDerivedSectionSourcesUpdaterProps } from '../../../types';

const Title = styled(CenterAlignedBox)``;

type FormValues = {
  sources: PageTemplateLite[];
};
export const PeoplePageDerivedSignalSourceUpdater = ({
  onCancel,
  node,
  onUpdate,
}: PeoplePageDerivedSectionSourcesUpdaterProps) => {
  const { templatesByPage, isLoading: isTemplatePageLoading } =
    useTemplatePageSources();

  const breadcrumbs = useMemo(() => {
    return [
      {
        title: node.attrs.title,
      },
    ];
  }, [node.attrs.title]);

  const [form] = Form.useForm<FormValues>();

  useEffect(() => {
    form.setFieldsValue({
      sources: node.attrs.config.sourcePageTemplates,
    });
  }, [form, node.attrs.config.sourcePageTemplates, templatesByPage]);

  const onSubmit = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        onUpdate(values.sources);
      })
      .catch(() => {});
  }, [form, onUpdate]);

  const removeSource = useCallback(
    (source: PageTemplateLite) => {
      const previousSources = form.getFieldsValue().sources;

      form.setFieldsValue({
        sources: previousSources.filter(
          ({ templateId }) => templateId !== source.templateId,
        ),
      });
    },
    [form],
  );

  return (
    <CustomLayoutDrawerContent>
      <CustomLayoutDrawerContent.Title
        breadcrumbs={<DrawerBreadCrumbs breadcrumbs={breadcrumbs} />}
      >
        <Title>
          <FontAwesomeIcon
            icon={faGlobe}
            size={20}
            iconSize={16}
            color="transparent"
          />
          <FormattedMessage id="sources" />
        </Title>
      </CustomLayoutDrawerContent.Title>
      <CustomLayoutDrawerContent.Content>
        {isTemplatePageLoading ? (
          <Loading />
        ) : (
          <>
            <Form layout="vertical" form={form}>
              <Form.Item
                label={
                  <FormattedMessage id="company_page_templates.fields.source.label" />
                }
                name="sources"
                requiredMark={'optional'}
                rules={[
                  {
                    required: true,
                    message: <FormattedMessage id="input.required" />,
                  },
                ]}
              >
                <PageTemplateSourceSelector
                  templatesByPage={templatesByPage}
                  checkboxColor="black"
                />
              </Form.Item>
              <Form.Item shouldUpdate>
                {() => {
                  const sources = form.getFieldsValue().sources ?? [];

                  return (
                    <PageTemplateSourceList
                      sources={sources}
                      removeSource={removeSource}
                    />
                  );
                }}
              </Form.Item>
            </Form>
          </>
        )}
      </CustomLayoutDrawerContent.Content>
      <CustomLayoutDrawerContent.Footer $rightAlignedAction>
        <Button onClick={onCancel}>
          <FormattedMessage id="back" />
        </Button>
        <Button
          variant="primary"
          leftIcon={<FontAwesomeIcon icon={faCheck} size={12} />}
          onClick={onSubmit}
        >
          <FormattedMessage id="done" />
        </Button>
      </CustomLayoutDrawerContent.Footer>
    </CustomLayoutDrawerContent>
  );
};
